import { Matrix } from 'mdatools/arrays';
import { mean, sd } from 'mdatools/stat';
import { qf } from 'mdatools/distributions';
import { pca_predict, pcamodel_get } from './wasm/dist/pca.js';


// constant values (colors, etc)
export const LINE_COLORS = {'cal': '#2679B2', 'pv': '#D22C2F', 'target': '#2679B2', 'alt': '#2679B2'};
export const FACE_COLORS = {'cal': '#f0f8ff', 'pv': '#fff8f0', 'target': '#f0f8ff', 'alt': '#f0f8ff'};
export const LABELS_COLOR = '#a0a0a0';
export const LABELS_COLOR_SELECTED = '#909090';


/**
 * Creates array with a sequence of values from `start` to `end`.
 * @param {number} start first value of sequence
 * @param {number} end last value of sequence
 * @param {number} by increment
 * @returns {Array} array with sequence.
 */
export function seq(start, end, by = 1) {
   const length = Math.round((end - start + 1) / by);
   const out = Array(length);
   for (let i = 0; i < length; i++) {
      out[i] = start + i * by;
   }
   return out;
}


/**
 * Creates arrays with object indices, labels and classes without outliers
 * @param {Object} dataset — JSON with dataset object.
 * @param {Array} outliers — array with object indices removed as outliers.
 * @returns {Array} array with three arrays — object indices, classes and labels
 */
function handle_outliers(dataset, outliers) {
   let indices, classes, labels;
   if (outliers.length > 0) {
      // filter the outlier out
      const nAll = dataset.values.nrows;
      const sampleIndices = dataset.objindices;
      const n = nAll - outliers.length;
      indices = Array(n);
      classes = Array(n);
      labels = Array(n);
      let j = 0;
      for (let i = 0; i < nAll; i++) {
         if (outliers.includes(i)) continue;
         indices[j] = sampleIndices[i];
         classes[j] = dataset.classes[i];
         labels[j] = dataset.objlabels[i]
         j = j + 1;
      }
   } else {
      indices = dataset.objindices;
      labels = dataset.objlabels;
      classes = dataset.classes;
   }

   return [indices, labels, classes];
}


/**
 * Generates unique pseudo-hash number based on current time and date.
 * @returns  string with the hash
 */
export function genhash() {
    const now = new Date();
    const uniqueId = `${now.getFullYear()}${(now.getMonth() + 1)
        .toString()
        .padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}${now
        .getHours()
        .toString()
        .padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now
        .getSeconds()
        .toString()
        .padStart(2, '0')}${now
        .getMilliseconds()
        .toString()
        .padStart(3, '0')}`;
    return uniqueId;
}


/**
 * Applies PCA model to a new dataset and adds additional attributes.
 *
 * @param {Promise} Module - WASM module (from Promise).
 * @param {Object} model - object with PCA model.
 * @param {Matrix} dataset - object with original dataset.
 * @param {Array} outliers - array with outliers.
 *
 * @returns {Object} object with PCA results
 */
export function app_pca_predict(Module, model, dataset, outliers) {

   // get arrays with sample indices, classeas and object labels without outliers
   const [objindices, objlabels, classes] = handle_outliers(dataset, outliers);

   // make predictions and add labels and indices
   const r = pca_predict(Module, model, dataset.values, outliers);
   r['hash'] = genhash();
   r['objindices'] = objindices;
   r['objlabels'] = objlabels;
   r['classes'] = classes;
   r['classNames'] = dataset.classNames;
   r['varlabels'] = dataset.varlabels;

   // model outcomes required for plots
   r['eigenvals'] = pcamodel_get(model, 'eigenvals');
   r['cNh'] = pcamodel_get(model, "cNh");
   r['cNq'] = pcamodel_get(model, "cNq");
   r['ch0'] = pcamodel_get(model, "ch0");
   r['cq0'] = pcamodel_get(model, "cq0");
   r['rNh'] = pcamodel_get(model, "rNh");
   r['rNq'] = pcamodel_get(model, "rNq");
   r['rh0'] = pcamodel_get(model, "rh0");
   r['rq0'] = pcamodel_get(model, "rq0");

   const n = model.nrows;
   r['T2lim'] =  (2 * (n - 1) / (n - 2)) * qf(0.95, 2, (n - 2));
   return r;
}


/**
 * Reverse columns of dataset and corresponding variable values.
 *
 * @param {Matrix} values - matrix with dataset values.
 * @returns {Matrix} matrix with reversed columns.
 */
export function reverseColumns(values) {

   const values_out = Matrix.zeros(values.nrows, values.ncols);
   for (let i = 0; i < values.ncols; i++) {
      const offset_in = values.nrows * i;
      const offset_out = values.nrows * (values.ncols - 1 - i);
      values_out.v.set(values.v.slice(offset_in, offset_in + values.nrows), offset_out);
   }

   return values_out;
}


/**
 * Converts string or array of strings to number(s).
 *
 * @param {Array|string} str - string or array of strings to convert to numbers.
 * @param {string} dec - character for decimal delimeter.
 *
 * @returns {Array|Number} number or array of numbers.
 */
function str2num(str, dec = '.') {

   if (Array.isArray(str) && str.length > 0) {
      return dec === '.' ? str.map(v => Number.parseFloat(v.trim())) :
         str.map(v => Number.parseFloat(v.trim().replace(dec, ".")));
   }

   return dec === '.' ? Number.parseFloat(str.trim()) :
      Number.parseFloat(str.trim().replace(dec, "."));
}


/**
 * Returns true if at least one value in array is NaN.
 * @param {Array} arr - array with values.
 * @returns logical value.
 */
function hasNaN(arr) {
   return arr.some(v => Number.isNaN(v));
}


/**
 * Removes white spaces and double quotes in a string (e.g. '" or """)
 * @param {String} s - string value
 * @returns {String}
 */
function clearLabels(s) {
   return s.replace('\t', '').trim().replace(/^(['"])(.*)\1$/, '$2');
}

/**
 * Takes CSV content as a string, cleans it and splits to separate rows
 * @param {String} csvString — string with CSV content.
 * @returns {Array} array of strings.
 */
function getCSVRows(csvString) {
   // remove BOM symbol if any
   let dataStr = csvString.replace(/^\uFEFF/gm, "").replace(/^\u00EF?\u00BB\u00BF/gm,"");

   // remove \r symbols
   dataStr = dataStr.replace(/\r/gm, "");

   // split string with data values into array of rows and filter empty rows
   return dataStr.split(/\n/).filter(v => v.trim().length > 1);
}


/**
 * Parse string with data from a CSV file and create a matrix with values.
 *
 * @param {string} strData - string with all data from the CSV file.
 * @param {string} sep - symbols used for separation of values (by default ',').
 * @param {string} header - type of header (first row in the data).
 * @param {boolean} hasLabels - logical, if first column must be treated as object labels or not.
 * @param {boolean} hasClasses - logical, if column next to labels must be treated as class names or not.
 * @param {string} varvaluesName - name for variable values (e.g. Wavelength).
 * @param {string} varvaluesUnits - units for variable values (e.g. nm).
 * @param {number} nresp - number of resoinse columns (reserved for future use).
 *
 * @returns {Object} JSON containing matrix with parsed values, header, values and decimal separators.
 */
export function parseCSV(strData, sep, header, hasLabels, hasClassNames, varvaluesName, varvaluesUnits, nresp = 0) {

   // TODO: reserved for response values in future
   const responses = null;

   // set default separator symbol
   if (sep === undefined) {
      sep = ",";
   }

   if (!(sep === ',' || sep === ';')) {
      throw new Error('Values separator can be either comma or semicolon.')
   }

   // setup decimal separator
   const dec = sep === ";" ? "," : ".";

   // split string with data values into array of rows and filter empty rows
   let rows = getCSVRows(strData);

   if (rows.length < 1) {
      throw new Error('Could not find non-empty rows with values, check your CSV file and try again.');
   }

   // get array with values from the first row
   const firstRowValues = rows[0].split(sep);

   // check if the first row contains header
   let startRow = 0;
   let headerLabels;
   const hasHeader = header !== 'no';
   if (hasHeader || (hasHeader === undefined && hasNaN(firstRow))) {
      headerLabels = firstRowValues;
      startRow = 1;
   }


   // number of rows and columns in future matrix
   const nrows = rows.length - startRow;
   if (nrows < 1) {
      throw new Error("Data file should contain at least 1 row with values.");
   }


   // check if first column contains row labels
   let labelsVarname;
   let startColumn;
   let objlabels;
   let objindices = Array(nrows);
   if (hasLabels) {
      labelsVarname = hasHeader ? clearLabels(headerLabels[0]) : '';
      objlabels = Array(nrows);
      startColumn = 1;
   } else{
      labelsVarname = '';
      objlabels = [];
      startColumn = 0;
   }

   // check if next column contains class names
   let classLabelsVarname;
   let classNames = [];
   let classes;
   if (hasClassNames) {
      classLabelsVarname = hasHeader ? clearLabels(headerLabels[startColumn]) : '';
      classes = Array(nrows);
      startColumn = startColumn + 1;
   } else {
      classLabelsVarname = '';
      classes = [];
   }

   // compute number of columns
   const ncols = firstRowValues.length - startColumn;
   if (ncols < 1) {
      throw new Error("Data file should contain at least 1 column with values.");
   }

   // set rows of data as columns of matrix - it is faster
   const data = Matrix.zeros(ncols, nrows);

   // find symbol used for decimals separator
   for (let r = startRow; r < nrows + startRow; r++) {

      // add row/object index
      const rind = r - startRow;
      objindices[rind] = rind;

      // split the row string into array of values
      let rowValuesAll = rows[r].split(sep)

      // if the first element is row label add it to labels array and increase the offset
      if (hasLabels) {
         objlabels[rind] = rowValuesAll[0];
         rowValuesAll.shift()
      }

      // if the next element is class/group label add it to classes array and increase the offset
      if (hasClassNames) {
         const className = rowValuesAll[0];
         classes[rind] = className;
         rowValuesAll.shift()

         // also add it to array of unique class names
         if (classNames.length === 0 || classNames.indexOf(className) < 0) {
            classNames.push(className);
         }
      }

      // for the rest of values, trim spaces, replace separator symbol with dot (.) and convert to numbers
      const rowValues = dec === '.' ?
         rowValuesAll.map(v => Number.parseFloat(v.trim())) :
         rowValuesAll.map(v => Number.parseFloat(v.trim().replace(dec, ".")));

      if (rowValues.some(v => Number.isNaN(v))) {
         throw new Error("Some of the values can not be converted to numbers, check your CSV file and try again.");
      }

      if (rowValues.length != ncols) {
         throw new Error("Wrong number of values in row #" + r);
      }

      data.v.set(new Float64Array(rowValues), (r - startRow) * ncols);
   }

   // generate row labels if they are not provided
   if (!hasLabels || objlabels.length !== nrows) {
      objlabels = Array(nrows);
      for (let i = 0; i < nrows; i++) {
         objlabels[i] = 'O' + (i + 1);
      }
   }

   // remove possible extra quotes around the labels and classes
   objlabels = objlabels.map(v => clearLabels(v));
   classes = classes.map(v => clearLabels(v));
   classNames = classNames.map(v => clearLabels(v));

   // handle variable labels, indices and values
   let varlabels;
   let varindices = Array(ncols);
   let varvaluesTemp = Array(ncols);
   if (headerLabels && headerLabels.length === ncols + startColumn) {
      varlabels = headerLabels.map(v => clearLabels(v)).slice(startColumn);
      for (let i = 0; i < ncols; i++) {
         varindices[i] = i;
         varvaluesTemp[i] = i + 1;
      }
   } else {
      varlabels = Array(ncols);
      for (let i = 0; i < ncols; i++) {
         varindices[i] = i;
         varlabels[i] = 'V' + (i + 1);
         varvaluesTemp[i] = i + 1;
      }
   }

   // parse variable labels as numeric values if it is expected
   let varvalues;
   if (header === 'values') {
      varvalues = dec === '.' ?
         varlabels.map(v => Number.parseFloat(v.trim())) :
         varlabels.map(v => Number.parseFloat(v.trim().replace(dec, ".")));
      if (varvalues.some(v => Number.isNaN(v))) {
         throw new Error("Some of the header values can not be converted to numbers, check your CSV file and try again.");
      }
   } else {
      varvalues = varvaluesTemp;
   }

   if (!Array.isArray(varvalues)) varvalues = [];
   if (!varvaluesName) varvaluesName = '';
   if (!varvaluesUnits) varvaluesUnits = '';

   varvaluesName = varvalues.length > 0 && varvaluesName.length > 0 ? varvaluesName : 'Variables';
   varvaluesUnits = varvalues.length > 0 && varvaluesUnits.length > 0 ? varvaluesUnits : '';
   const varAxisTitle = varvaluesName + (varvaluesUnits.length > 0 ? ', ' + varvaluesUnits : '');

   const varrev = varvalues[1] < varvalues[0];
   const out = {
      values: varrev ? reverseColumns(data.t()) : data.t(),
      header,
      hasLabels,
      hasClassNames,
      labelsVarname,
      classLabelsVarname,
      objlabels,
      objindices,
      varlabels : varrev ? varlabels.reverse() : varlabels,
      varvalues : varrev ? varvalues.reverse() : varvalues,
      varindices,
      varrev,
      classes,
      responses,
      classNames,
      sep,
      dec,
      varvaluesName,
      varvaluesUnits,
      varAxisTitle
   };

   return out;
}


/**
 * Tries to detect if first row of CSV file contains header.
 *
 * @param {Array} rows_num_values - arrat of CSV rows, split to separate values and converted to numbers.
 *
 * @returns {boolean} true if header is detected.
 */
function detectHeader(rows_num_values) {

   // simple guesser based on presence of non-text values in the first row (check values 3 and 4)
   if (hasNaN(rows_num_values[0].slice(2))) {
      return true;
   }

   // if the values are numeric check
   // how different the values in the first row from the other ones
   // for columns starting fom 3
   const max_ncols = rows_num_values[0].length;
   const z_arr = [];
   for (let c = 2; c < max_ncols; c++) {

      // get values from the column and rows 2...4 (or less)
      let cv = [];
      for (let i = 1; i < rows_num_values.length; i++) {
         cv.push(rows_num_values[i][c]);
      }

      // compute absolute z-score and compare with 5
      const v1 = rows_num_values[0][c]
      const m = mean(cv);
      const s = sd(cv);
      const z = Math.abs((v1 - m) / s);
      z_arr.push(z);
   }

   // if all of them above 5 then most likely the first row is header
   return z_arr.every(v => v > 5);
}

/**
 * Detects symbol used to separate values in CSV file.
 *
 * @param {Array} rows - array with rows (each row is a string).
 *
 * @returns {Array} array with three values, symbols for values and decimals separators and number of columns.
 */
function detectSeparators(rows) {

   // guess values separator
   const sep1 = ',';
   const v11 = rows[0].split(sep1);
   const v12 = rows[1].split(sep1);
   const l11 = v11.length;
   const l12 = v12.length;

   const sep2 = ';';
   const v21 = rows[0].split(sep2);
   const v22 = rows[1].split(sep2);
   const l21 = v21.length;
   const l22 = v22.length;

   // check which of the two separators give longer and more consistent
   // arrays with values
   let ncols, v1, v2, sep;
   if (l11 > 1 && l12 > 1 && l11 === l12 && l11 > l21) {
      sep = sep1;
      ncols = l11;
      v1 = v11;
      v2 = v12;
   } else if (l21 > 1 && l22 > 1 && l21 === l22) {
      sep = sep2;
      ncols = l12;
      v1 = v21;
      v2 = v22;
   } else {
      throw new Error('Values in the CSV file should be separated by coma or by semicolon.');
   }

   // decimal separator
   const dec = sep === ',' ? '.' : ',';
   return [sep, dec, ncols];
}

/**
 * Parses CSV file string to detect main parameters (separators, header, presence of row and class labels).
 *
 * @param {String} csvString - string with CSV file content.
 *
 * @returns {Array} array with parameters.
 */
export function parseCSVParams(csvString) {

   // split string with CSV content to separate rows and check size
   const rows = getCSVRows(csvString);
   const nrows = rows.length;
   if (nrows < 3) {
      throw new Error('It looks like the dataset has less then three rows, minimum three is needed, check your CSV file and try again.');
   }

   // detect values and decimal separators
   const [sep, dec, ncols] = detectSeparators(rows);
   const max_ncols = Math.min(ncols, 5);

   // split first several rows to values and keep maximum 5 values in each
   const max_nrows = Math.min(nrows, 5);
   let rows_values = rows.slice(0, max_nrows).map(v => v.split(sep).slice(0, max_ncols));

   // additionally clear the values
   rows_values = rows_values.map(v => v.map(v => clearLabels(v)));

   // turn values to numeric and check that values starting from row 2 and column 3 are numeric
   const rows_num_values = rows_values.map(v => str2num(v.slice(), dec));
   for (let i = 1; i < max_nrows; i++) {
      if (hasNaN(rows_num_values[i].slice(2))) {
         throw new Error('Some of the data values expected to be numeric are not, check your CSV file.');
      }
   }

   // detect if the data has header
   const hasHeader = detectHeader(rows_num_values);
   let headerShift = 0;

   const r1 = hasHeader ? 1 : 0;
   const r2 = r1 + 1;

   // values from first column of rows 2 and 3 starting from header
   const v21 = rows_values[r1][0];
   const v31 = rows_values[r2][0];

   // numeric values from first column of rows 2 and 3
   const nv21 = rows_num_values[r1][0];
   const nv31 = rows_num_values[r2][0];

   // numeric values from second column of rows 2 and 3
   const nv22 = rows_num_values[r1][1];
   const nv32 = rows_num_values[r2][1];

   // detect columns with row and class labels
   let hasLabels = false;
   let hasClassLabels = false;
   if (Number.isNaN(nv21) && Number.isNaN(nv31) && v21 !== v31) {
      // guess if row labels present: if first value of rows 2 and 3 are not numbers
      // and are not equal to each other than they are possible row labels
      hasLabels = true;
      headerShift = headerShift + 1;
   } else if (Number.isNaN(nv22) && Number.isNaN(nv32) && v21 !== v31) {
      // now check if values from the second column are non numeric,
      // and at the same time values from the first column are not equal, if so
      // the second column possible contains class names and hence the first column
      // consist of row labels
      hasLabels = true;
      hasClassLabels = true;
      headerShift = headerShift + 2;
   }


   // guess class labels - we check next column and if both values of rows 2 and 3
   // are not numeric, we assume they are class labels
   if (!hasClassLabels) {
      const ncol = hasLabels ? 1 : 0;

      // numeric values from first column of rows 2 and 3
      const nv21 = rows_num_values[r1][ncol];
      const nv31 = rows_num_values[r2][ncol];

      hasClassLabels = hasNaN([nv21, nv31]);
      headerShift = headerShift + hasClassLabels;
   }

   // gues if header contains text labels of numeric values
   let header = 'no';
   if (hasHeader) {
      const v1 = headerShift > 0 ? rows_values[0].slice(headerShift) : rows_values[0];
      const nv1 = headerShift > 0 ? rows_num_values[0].slice(headerShift) : rows_num_values[0];
      header = hasNaN(nv1) ? 'labels' : 'values';
   }

   return [sep, hasLabels, hasClassLabels, header];
}

export function toCSV(dataset) {

   // get values, reverse columns if needed and then transpose
   const values = dataset.varrev ? reverseColumns(dataset.values).t() : dataset.values.t();
   const sep = dataset.sep;
   const dec = dataset.dec;

   let header_values = null;
   if (dataset.header !== 'no') {
      header_values = dataset.header === 'values' ? dataset.varvalues.slice() : dataset.varlabels.slice();
   }

   if (header_values && dataset.varrev) {
      header_values = header_values.reverse()
   }

   if (header_values && dataset.hasClassNames) {
      header_values = [dataset.classLabelsVarname, ...header_values];
   }

   if (header_values && dataset.hasLabels) {
      header_values = [dataset.labelsVarname, ...header_values];
   }


   let csvString = header_values ? header_values.join(sep) : '\n';
   for (let i = 0; i < values.ncols; i++) {
      let rowStr = values.getcolref(i + 1).join(sep);

      if (dec !== '.') {
         rowStr = rowStr.replace('.', dec);
      }

      if (dataset.hasClassNames) {
         rowStr = dataset.classes[i] + dataset.sep + rowStr;
      }

      if (dataset.hasLabels) {
         rowStr = dataset.objlabels[i] + dataset.sep + rowStr;
      }

      csvString = csvString + '\n' + rowStr;
   }
   csvString = csvString + '\n';

   if (dec !== '.') {
      csvString = csvString.replace(/\./g, dec);
   }

   return csvString;
}

export function downloadData(dataset, filename) {
   const csvString = toCSV(dataset);
   const blob = new Blob([csvString], { type: 'text/csv' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', `${filename}.csv`);
   a.click()
}

/**
 * Create a new matrix which contains values from X except the excluded variables.
 *
 * @param {Matrix} X - matrix with original values.
 * @param {Array} varlist - list of variables to exclude.
 * @returns {Matrix}
 */
export function excludeVars(dataset, varlist) {

   const values = dataset.values;
   const varvalues = dataset.varvalues;
   const varlabels = dataset.varlabels;
   const varindices = dataset.varindices;

   const nrows = values.nrows;
   const ncols = values.ncols;
   const ncolsNew = ncols - varlist.length
   const valuesNew = Matrix.zeros(nrows, ncolsNew);
   const varvaluesNew = Array(ncolsNew);
   const varlabelsNew = Array(ncolsNew);
   const varindicesNew = Array(ncolsNew);

   let c = 0;
   for (let i = 0; i < ncols; i++) {
      if (varlist.includes(i)) continue;
      const offset_0 = nrows * i;
      const offset_1 = nrows * c;
      valuesNew.v.set(values.v.slice(offset_0, offset_0 + nrows), offset_1);
      varvaluesNew[c] = varvalues[i];
      varlabelsNew[c] = varlabels[i];
      varindicesNew[c] = varindices[i];
      c = c + 1;
   }

   return {
      values: valuesNew,
      header: dataset.header,
      hasLabels: dataset.hasLabels,
      hasClassNames: dataset.classNames,
      labelsVarname: dataset.labelsVarname,
      classLabelsVarname: dataset.classLabelsVarname,
      objlabels: dataset.objlabels,
      objindices: dataset.objindices,
      varlabels : varlabelsNew,
      varvalues : varvaluesNew,
      varindices: varindicesNew,
      varrev: dataset.varrev,
      classes: dataset.classes,
      responses: dataset.responses,
      classNames: dataset.classNames,
      sep: dataset.sep,
      dec: dataset.dec,
      varvaluesName: dataset.varvaluesName,
      varvaluesUnits: dataset.varvaluesUnits,
      varAxisTitle: dataset.varAxisTitle,
      fileName: dataset.fileName
   };
}


/**
 * Converts array of numbers to intervals.
 * @param {Array} arr - array of numbers.
 * @returns {String}
 */
export function convertToIntervals(arr) {
  if (!arr.length) return '';

  // Sort the array in ascending order
  arr.sort((a, b) => a - b);

  let result = [];
  let start = arr[0];
  let end = start;

  for (let i = 1; i < arr.length; i++) {
    if (arr[i] === end + 1) {
      // continue the sequence
      end = arr[i];
    } else {
      // sequence broken, push current range to result
      if (start === end) {
        result.push(`${start}`);
      } else {
        result.push(`${start}-${end}`);
      }
      // reset start and end for the next sequence
      start = arr[i];
      end = start;
    }
  }

  // push the last range or number
  if (start === end) {
    result.push(`${start}`);
  } else {
    result.push(`${start}-${end}`);
  }

  return result.join(" ");
}
